import React, { useRef, useState } from 'react';
import { Upload, Link2, Trash2, Loader2, Video, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import { uploadMedia, parseEmbed, mediaSrc, MAX_UPLOAD_MB } from './uploadMedia';

const MediaThumb = ({ item, onCaption, onRemove, testId }) => (
  <div data-testid={testId} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
    <div className="aspect-video bg-slate-900 flex items-center justify-center">
      {item.type === 'image' && <img src={mediaSrc(item.url)} alt={item.caption || ''} className="w-full h-full object-cover" />}
      {item.type === 'video' && <video src={mediaSrc(item.url)} className="w-full h-full object-cover" muted preload="metadata" />}
      {item.type === 'embed' && <iframe title={item.caption || 'video'} src={item.url} className="w-full h-full" allowFullScreen />}
    </div>
    <div className="p-2 flex items-center gap-2">
      {item.type === 'image' ? <ImageIcon className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <Video className="h-4 w-4 text-slate-400 flex-shrink-0" />}
      <Input data-testid={`${testId}-caption`} value={item.caption || ''} placeholder="Caption (optional)" className="h-8 text-xs" onChange={(e) => onCaption(e.target.value)} />
      <button type="button" data-testid={`${testId}-remove`} onClick={onRemove} className="text-slate-400 hover:text-red-600 flex-shrink-0" aria-label="Remove">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export const MediaManager = ({ items = [], onChange, propertyId, testId = 'media', accept = 'image/*,video/*' }) => {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [link, setLink] = useState('');

  const update = (i, patch) => onChange(items.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      try {
        setProgress({ name: file.name, pct: 0 });
        const result = await uploadMedia(file, { token, propertyId, onProgress: (pct) => setProgress({ name: file.name, pct }) });
        onChange((prev) => [...prev, { id: result.id, type: result.type, url: result.url, caption: '' }]);
        toast({ title: `${file.name} uploaded` });
      } catch (e) {
        toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
      }
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const addLink = () => {
    const embed = parseEmbed(link.trim());
    if (!embed) return toast({ title: 'Paste a YouTube or Vimeo link', variant: 'destructive' });
    onChange((prev) => [...prev, { id: `embed-${Date.now()}`, type: 'embed', url: embed, caption: '' }]);
    setLink('');
  };

  return (
    <div className="space-y-3" data-testid={`${testId}-manager`}>
      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {items.map((item, i) => (
            <MediaThumb key={item.id} item={item} testId={`${testId}-item-${i}`}
              onCaption={(caption) => update(i, { caption })}
              onRemove={() => onChange(items.filter((_, idx) => idx !== i))} />
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input ref={inputRef} type="file" accept={accept} multiple className="hidden" data-testid={`${testId}-file-input`} onChange={(e) => handleFiles(e.target.files)} />
        <Button type="button" data-testid={`${testId}-upload-btn`} variant="outline" onClick={() => inputRef.current?.click()} disabled={!!progress}>
          {progress ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {progress ? `Uploading ${progress.name} ${progress.pct}%` : `Upload photos / videos (max ${MAX_UPLOAD_MB}MB)`}
        </Button>
        <div className="flex gap-2 flex-1">
          <Input data-testid={`${testId}-embed-input`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste YouTube / Vimeo link" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLink())} />
          <Button type="button" data-testid={`${testId}-embed-btn`} variant="outline" onClick={addLink}><Link2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {progress && <div className="h-1.5 bg-slate-200 rounded"><div className="h-1.5 bg-amber-500 rounded transition-[width]" style={{ width: `${progress.pct}%` }} /></div>}
    </div>
  );
};
